import { useState, type InputHTMLAttributes } from 'react'
import { T, tt, type LatLng, type ZoneOutline } from '@agos/shared'
import { cn,
  AddressField,
} from '@agos/ui'
import { ChevronDown, Loader2, Mail, MapPin, Phone, Sparkles, User } from 'lucide-react'

import { DeliveryPinMap } from './DeliveryPinMap'

/**
 * The *Zákazník* card, ported from legacy `new-order.tsx:1145`.
 *
 * **Where it sits is the point.** Legacy puts it in the main column, directly under the order-type row
 * and above the products; AGOS had the same three fields stacked down the side of the cart panel, so
 * taking a telephone order meant typing into the far right of the screen and then crossing back to the
 * menu. The owner's note is *"ako v starom manažéri"*, and this is what the old manager does.
 *
 * **Telephone first**, because it is the field that fills the others (§9.14's lookup) and because it is
 * the first thing said on the telephone. Name and e-mail beside it, address below when the channel
 * needs one.
 */
const FIELD =
  'w-full pl-9 pr-4 py-2.5 rounded-xl bg-background border text-sm outline-none focus:ring-2 focus:ring-primary/20'

export function CustomerPanel({
  phone,
  onPhoneChange,
  name,
  onNameChange,
  email,
  onEmailChange,
  address,
  onAddressChange,
  onAddressResolved,
  onAddressUnresolved,
  showAddress,
  looking,
  autofilledFrom,
  pastAddresses,
  onPickAddress,
  deliveryPoint,
  depot,
  zones,
  onPinMoved,
  onPinAddress,
}: {
  phone: string
  onPhoneChange: (value: string) => void
  name: string
  onNameChange: (value: string) => void
  email: string
  onEmailChange: (value: string) => void
  address: string
  onAddressChange: (value: string) => void
  /** Coordinates for a picked suggestion, so the caller can choose the delivery zone from them. */
  onAddressResolved?: (place: { lat: number; lon: number; formattedAddress: string }) => void
  /**
   * A picked suggestion whose coordinates could not be resolved - §9.372.
   *
   * Not the same as nothing having happened: the address has already changed, so a zone settled
   * from the *previous* address's point is now attached to a different street.
   */
  onAddressUnresolved?: (address: string) => void
  showAddress: boolean
  looking: boolean
  /** Who the answer was about, or `null` when nothing was filled in. */
  autofilledFrom: string | null
  /** Offered only when there is more than one; a single address is filled straight in. */
  pastAddresses: string[]
  onPickAddress: (value: string) => void
  /**
   * Where the delivery goes, and where the map opens without it - §9.694, the owner's #13.
   *
   * The *Mapa* panel is drawn only when the caller handles a drop as well, so a caller that passes none of
   * the four gets the card it always had.
   */
  deliveryPoint?: LatLng | null
  depot?: LatLng | null
  /** §9.802 - the delivery zones the map draws under the pin, to look at only. */
  zones?: readonly ZoneOutline[]
  onPinMoved?: (point: LatLng) => void
  onPinAddress?: (address: string) => void
}) {
  const filled = autofilledFrom !== null
  const [addressesOpen, setAddressesOpen] = useState(false)

  return (
    <div className="bg-card border border-border rounded-2xl p-3 space-y-2" data-customer-panel>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          <T>Zákazník</T>
        </p>
        {looking && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-lookup-busy>
            <Loader2 className="w-3 h-3 animate-spin" /> <T>Hľadám zákazníka…</T>
          </span>
        )}
        {filled && !looking && (
          <span
            data-autofilled
            className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2 py-0.5"
          >
            <Sparkles className="w-3 h-3" /> <T>Doplnené z databázy</T>
          </span>
        )}
      </div>

      {/* §9.114 - three across on a till-sized screen, so the four fields are one row rather than
        * two. Measured: a third of a 900 px screen went to this card and the menu started below the
        * fold.
        *
        * §9.267 — and until now it was three columns with nothing ever wider than two of them, so a
        * **third of the card was blank** at 820, 1280 and 1600 px: both wide fields carried
        * `sm:col-span-2` and no `md:` span beside it. The spans below finish the sentence this
        * comment was already making.
        *
        * **`lg:` rather than `md:`, which is the half that un-clips the name.** `md:` is a 768 px
        * *viewport* breakpoint and the sidebar takes 256 px of it, so a tablet at 820 was splitting
        * 516 px three ways — 158 px columns, into which *Meno zákazníka* needs 108 px of text with
        * 106 px of room. A container query would say this properly; the tree has no precedent for
        * one and this is not the entry to introduce it in. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            value={phone}
            onChange={(event) => onPhoneChange(event.target.value)}
            placeholder={tt('Telefónne číslo')}
            aria-label={tt('Telefón')}
            type="tel"
            inputMode="tel"
            data-customer-phone
            className={cn(FIELD, 'border-border')}
          />
        </div>

        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            placeholder={tt('Meno zákazníka')}
            aria-label={tt('Meno zákazníka')}
            data-customer-name
            className={cn(FIELD, filled ? 'border-emerald-400/50 bg-emerald-400/5' : 'border-border')}
          />
        </div>

        {/* One column once there are three: phone, name and e-mail are then genuinely one row. */}
        <div className="sm:col-span-2 lg:col-span-1 relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            placeholder={tt('E-mailová adresa (voliteľné)')}
            aria-label={tt('E-mail')}
            type="email"
            inputMode="email"
            data-customer-email
            className={cn(
              FIELD,
              filled && email !== '' ? 'border-emerald-400/50 bg-emerald-400/5' : 'border-border',
            )}
          />
        </div>

        {showAddress && (
          <div className="sm:col-span-2 lg:col-span-3">
            {/* Suggested rather than typed blind. The key arrived on 2026-08-03 (BLOCKED #7) and the
                autocomplete had been parked on its absence ever since; `AddressField` was already
                built and wired into the depot field only. A phone order is where an address is most
                often wrong, because it is heard rather than read.
                The delivery *fee* still comes from the zone picker, deliberately - the zones are
                named after the villages the caller is already naming, which is a reason that never
                depended on the key. This adds a suggestion, it does not replace that. */}
            <AddressField
              value={address}
              onChange={onAddressChange}
              onResolved={onAddressResolved}
              onUnresolved={onAddressUnresolved}
              placeholder={tt('Adresa doručenia')}
              ariaLabel={tt('Adresa')}
              inputProps={{ 'data-customer-address': '' } as InputHTMLAttributes<HTMLInputElement>}
            />
            {/* §9.694 - where it goes, and a pin that can be moved to the door. */}
            {onPinMoved !== undefined && onPinAddress !== undefined && (
              <DeliveryPinMap
                point={deliveryPoint ?? null}
                depot={depot ?? null}
                zones={zones ?? []}
                onPinMoved={onPinMoved}
                onPinAddress={onPinAddress}
              />
            )}
          </div>
        )}
      </div>

      {/* More than one address on this number: offered rather than guessed. Filling the newest silently
        * would deliver to the flat somebody moved out of, and the person on the telephone is right
        * there to be asked which. */}
      {showAddress && pastAddresses.length > 1 && (
        <div className="relative" data-past-addresses>
          {/* §9.113 - a disclosure, floated. One line per address with its number is the owner's
            * point 2; costing the panel no height is their point 3. Measured before this: fifteen
            * stacked rows made the card 809 px on a 900 px screen and pushed the whole menu off it.
            * Legacy positions the list absolutely for exactly that reason. */}
          <button
            type="button"
            data-past-addresses-toggle
            aria-expanded={addressesOpen}
            onClick={() => setAddressesOpen((open) => !open)}
            className="flex items-center gap-1.5 text-xs text-primary font-medium px-2.5 py-1 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors"
          >
            <ChevronDown
              className={cn('w-3.5 h-3.5 transition-transform', addressesOpen && 'rotate-180')}
            />
            <T>Vybrať z histórie</T> ({pastAddresses.length})
          </button>
          {addressesOpen && (
            <div className="absolute top-full left-0 mt-1 z-20 min-w-[280px] max-w-lg max-h-64 overflow-y-auto bg-popover border border-border rounded-xl shadow-lg">
              {pastAddresses.map((past) => (
                <button
                  key={past}
                  type="button"
                  data-past-address
                  onClick={() => {
                    onPickAddress(past)
                    setAddressesOpen(false)
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors',
                    past === address
                      ? 'bg-primary/10 text-primary font-bold'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                  )}
                >
                  <MapPin className="w-3.5 h-3.5 shrink-0" />
                  {/* Not truncated: the number is at the end of the string, so an ellipsis would cut
                    * off the one part being read for. */}
                  <span className="whitespace-nowrap">{past}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  )
}
